<template>
  <v-layout row align-start justify-space-between wrap>
    <div class="input-field-container small">
      <label class="required">{{ $tc("Code", 1) }}</label>
      <input
        class="input-field"
        id="code"
        type="text"
        v-model="customField.code"
        :disabled="disableFields"
      />
    </div>

    <div class="input-field-container small">
      <label class="required"> {{ $tc("Label", 1) }} </label>
      <input
        class="input-field"
        id="label"
        type="text"
        v-model="customField.label"
        :disabled="disableFields"
      />
    </div>

    <div class="input-field-container small">
      <label class="required">{{ $tc("Object", 1) }}</label>
      <treeselect
        v-model="objectValue"
        :options="customFieldObjectOptions"
        disableFuzzyMatching
        :disabled="disableFields"
        disableBranchNodes
      />
    </div>

    <div class="input-field-container small">
      <label class="required">{{ $tc("Type", 1) }}</label>
      <treeselect
        v-model="customField.input_type"
        :options="fieldTypes"
        disableFuzzyMatching
        :disabled="disableFields"
      />
    </div>

    <div class="input-field-container small">
      <label>{{ $t("List") }}</label>
      <treeselect
        v-model="customField.list_name"
        :options="customLists"
        disableFuzzyMatching
        :disabled="
          disableFields ||
            !(
              customField.input_type === 'select' ||
              customField.input_type === 'multiselect'
            )
        "
      />
    </div>

    <div class="input-field-container small" />

    <div class="input-field-container small">
      <label>{{ $t("DefaultValue") }}</label>
      <input
        v-if="
          customField.input_type === 'text' ||
            customField.input_type === 'textarea' ||
            customField.input_type === 'float' ||
            customField.input_type === 'int'
        "
        v-model="customField.default_value"
        class="input-field"
        id="default"
        :type="
          customField.input_type === 'text' ||
          customField.input_type === 'textarea'
            ? 'text'
            : 'number'
        "
        :disabled="disableFields || customField.input_type === undefined"
      />
      <treeselect
        v-else-if="customField.input_type === 'checkbox'"
        v-model="customField.default_value"
        :options="booleanOptions"
        disableFuzzyMatching
        :disabled="disableFields || customField.input_type === undefined"
      />
      <v-menu
        v-else-if="customField.input_type === 'date'"
        v-model="startDateMenu"
        :close-on-content-click="false"
        :nudge-right="40"
        lazy
        transition="scale-transition"
        offset-y
        full-width
        min-width="290px"
        :disabled="disableFields || customField.input_type === undefined"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-model="customField.default_value"
            append-icon="event"
            readonly
            clearable
            v-on="on"
            style="width: 100%"
          />
        </template>
        <v-date-picker
          v-model="customField.default_value"
          @input="startDateMenu = false"
          :max="today"
          color="accent"
          no-title
        />
      </v-menu>
      <input-field
        v-else-if="customField.input_type === 'datetime'"
        :label="''"
        v-model="customField.default_value"
        input-type="datetime"
        size="large"
        class="input-field"
        id="datetimepicker"
        :field-disabled="disableFields || customField.input_type === undefined"
        style="padding-bottom: 0px; display: flex; align-items: center"
      />
      <treeselect
        v-else-if="
          customField.input_type === 'select' ||
            customField.input_type === 'multiselect'
        "
        v-model="customField.default_value"
        :options="list"
        disableFuzzyMatching
        :multiple="customField.input_type === 'multiselect' ? true : false"
        :disabled="
          disableFields ||
            customField.input_type === undefined ||
            customField.list_name === undefined
        "
      />
      <input v-else class="input-field" disabled="true" />
    </div>

    <div class="input-field-container small">
      <label>{{ $t("Required") }}</label>
      <v-checkbox
        id="required"
        :disabled="disableFields"
        :ripple="false"
        color="#4fa2b7"
        v-model="customField.required"
        disableFuzzyMatching
      />
    </div>

    <div class="input-field-container small">
      <label class="required">{{ $tc("Size", 1) }}</label>
      <treeselect
        v-model="customField.size"
        :options="customFieldSizeOptions"
        disableFuzzyMatching
        :disabled="disableFields"
      />
    </div>

    <div class="input-field-container small">
      <label> {{ $t("Index") }} </label>
      <input
        class="input-field"
        id="index"
        type="number"
        v-model="customField.index"
        :disabled="disableFields"
      />
    </div>
  </v-layout>
</template>

<script>
import { listsMixin } from "@/mixins/listsMixin";
import { customFieldsMixin } from "@/mixins/customFieldsMixin";
import { materialFamilyMixin } from "@/mixins/management/materialFamilyMixin";
import InputField from "@/components/Home/Forms/_Molecules/InputField";

export default {
  name: "CustomFieldFields",

  components: {
    InputField
  },

  mixins: [listsMixin, materialFamilyMixin, customFieldsMixin],

  props: {
    /**
     * The specific fields values passed by the parent component
     */
    customField: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      startDateMenu: false,
      objectValue: null
    };
  },

  computed: {
    /**
     * Indicates if the fields should be disabled or not
     * @return {boolean}
     */
    disableFields() {
      return this.$route.name === "custom-field-view";
    },

    booleanOptions() {
      return [
        {
          id: "true",
          label: this.$i18n.t("True")
        },
        {
          id: "false",
          label: this.$i18n.t("False")
        }
      ];
    },

    customFieldObjectOptions() {
      return [
        {
          id: "families",
          label: this.$i18n.tc("Family", 2),
          children: this.objectsAndMaterialFamilies
        },
        {
          id: "forms",
          label: this.$i18n.tc("Form", 2),
          children: this.forms
        }
      ];
    },

    today() {
      let today = new Date();
      return today.toISOString();
    }
  },

  watch: {
    "customField.input_type"() {
      this.customField.default_value = undefined;
      this.customField.list_name = undefined;
    },

    "customField.list_name"() {
      this.list = [];
      let listUuid = this.getListUuid(this.customField.list_name);
      this.customField.default_value = undefined;
      if (listUuid) {
        this.getCustomList(listUuid);
      }
    },

    objectValue() {
      if (this.forms.find(form => form.id === this.objectValue)) {
        this.customField.object_type = null;
        this.customField.table = this.objectValue;
      } else if (
        this.objectsAndMaterialFamilies.find(
          family => family.id === this.objectValue
        )
      ) {
        this.customField.object_type = this.objectValue;
        this.customField.table = "fc_stock_management_materials";
      } else if (!this.objectValue) {
        this.customField.object_type = null;
        this.customField.table = null;
      }
    }
  },

  mounted() {
    this.getCustomLists(this.customField);
    this.objectValue = this.customField.object_type
      ? this.customField.object_type
      : this.customField.table;
  }
};
</script>

<style scoped>
.text-in-between {
  margin: 0 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/SystemManagement/SystemSettings/CustomFields/CustomFieldFields.vue